import {
  WBFlex,
  WBIcon,
  WBAutocomplete,
  WBTextFieldV2,
  WBTypography,
  WBBox,
  WBMenuItem,
} from '@admiin-com/ds-web';
import { MobileDatePicker } from '@mui/x-date-pickers';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import React from 'react';
import { styled, useTheme } from '@mui/material';
import { DATE_FORMATS } from '@admiin-com/ds-common';
import { t } from 'i18next';
export type DueDateSelectorV2Props = {
  onChange: (value: string) => void;
  variant?: 'standard' | 'outlined' | 'filled';
  error?: boolean;
  helperText?: string;
  value?: string;
  disabled?: boolean;
  placeholder?: string;
  type?: 'date' | 'datetime-local';
  label?: string;
  fullWidth?: boolean;
};
const today = DateTime.now().setZone('Australia/Sydney');
const daysToString = (days: number) =>
  days === 0
    ? t('today', { ns: 'taskbox' })
    : t('days', { ns: 'taskbox', day: days });

const dateOptions = [0, 7, 14, 30].map((days) => {
  const date = today.plus({ days: days });
  return {
    value: date.toFormat(DATE_FORMATS.BACKEND_DATE),
    label: `${daysToString(days)} (${date.toFormat('dd MMM')})`,
    isCustom: false,
  };
});

const allOptions = [
  ...dateOptions,
  {
    value: 'custom',
    label: t('customDate', { ns: 'taskbox' }),
    isCustom: true,
  },
];

const renderDate = (date: string) => {
  const dueDateFormat = DateTime.fromFormat(
    date,
    DATE_FORMATS.BACKEND_DATE
  ).toFormat(DATE_FORMATS.DUE_DATE);

  // Calculate days difference using Australia/Sydney timezone consistently
  const targetDate = DateTime.fromFormat(date, DATE_FORMATS.BACKEND_DATE)
    .setZone('Australia/Sydney')
    .startOf('day');
  const todaySydney = DateTime.now().setZone('Australia/Sydney').startOf('day');
  const days = Math.round(targetDate.diff(todaySydney, 'days').days);

  return `${dueDateFormat} (${daysToString(days)})`;
};

// Function to parse manual date input (DD/MM/YYYY)
const parseManualDate = (input: string): string | null => {
  const normalised = input.trim();
  const tryFormats = ['dd/MM/yyyy', 'd/M/yyyy'];

  for (const fmt of tryFormats) {
    const parsed = DateTime.fromFormat(normalised, fmt);
    if (parsed.isValid) {
      return parsed.toFormat(DATE_FORMATS.BACKEND_DATE);
    }
  }
  return null;
};

const formatInput = (input: string): string => {
  const digits = input.replace(/\D/g, '');

  if (digits.length < 4) return digits; // Not enough for day+month yet

  // Day + month present – decide whether user gave us a year
  const day = digits.slice(0, 2);
  const month = digits.slice(2, 4);
  const yearPart = digits.slice(4, 8);
  const year = yearPart.length === 0 ? today.year.toString() : yearPart;

  return `${day}/${month}/${year}`;
};

export const DueDateSelectorV2 = React.forwardRef<any, DueDateSelectorV2Props>(
  (props: DueDateSelectorV2Props, ref) => {
    const {
      onChange,
      variant = 'standard',
      error,
      helperText,
      value,
      disabled,
      placeholder,
      fullWidth,
      label,
    } = props;

    const { t } = useTranslation();
    const inputRef = React.useRef<HTMLInputElement>(null);
    const [open, setOpen] = React.useState(false);
    const theme = useTheme();
    const [inputValue, setInputValue] = React.useState('');
    React.useEffect(() => {
      if (value) {
        const formatted = !open
          ? renderDate(value)
          : DateTime.fromFormat(value, DATE_FORMATS.BACKEND_DATE).toFormat(
              DATE_FORMATS.USER_DATE
            );
        setInputValue(formatted);
      } else {
        setInputValue('');
      }
    }, [value, open]);

    const handleOnChange = (newValue: any) => {
      console.log('newValue', newValue);
      if (
        newValue &&
        typeof newValue === 'object' &&
        newValue !== null &&
        (newValue as any).isCustom
      ) {
        // Handle custom date selection - open date picker
        inputRef.current?.click();
        return;
      } else if (
        newValue &&
        typeof newValue === 'object' &&
        newValue !== null
      ) {
        // Handle predefined option selection
        onChange((newValue as any).value);
      } else if (typeof newValue === 'string') {
        // Handle manual text input
        const parsedDate = parseManualDate(newValue);
        if (parsedDate) {
          onChange(parsedDate);
        } else {
          // If parsing fails, pass the raw value
          onChange(newValue);
        }
      } else if (newValue === null) {
        onChange('');
      }
    };

    return (
      <>
        <WBAutocomplete
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          ref={ref}
          freeSolo
          disabled={disabled}
          value={value}
          filterSelectedOptions={false}
          filterOptions={(options) => {
            return options;
          }}
          inputValue={inputValue}
          options={allOptions}
          onInputChange={(_, newInput) => {
            const masked = formatInput(newInput);
            setInputValue(masked);

            const parsed = parseManualDate(masked);
            if (parsed) handleOnChange(masked); // propagate when valid
          }}
          popupIcon={<WBIcon name="ChevronDown" size={1.3} color={'black'} />}
          fullWidth={fullWidth}
          getOptionLabel={(option: any) => {
            if (typeof option === 'string') return option;
            return option?.label || '';
          }}
          isOptionEqualToValue={(option: any, value: any) => {
            return false;
          }}
          onChange={(event, newValue, reason) => {
            handleOnChange(newValue);
          }}
          renderInput={(params) => (
            <WBTextFieldV2
              {...params}
              label={label}
              variant={variant}
              error={error}
              helperText={helperText}
              placeholder={
                placeholder ||
                t('selectDate', { ns: 'taskbox' }) + ' (DD/MM/YYYY)'
              }
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <WBBox sx={{ marginRight: theme.spacing(1) }}>
                    <WBIcon
                      color={theme.palette.grey[500]}
                      name="Calendar"
                      size={2}
                    />
                  </WBBox>
                ),
              }}
            />
          )}
          renderOption={(props, option) => {
            return (
              <StyledOption
                {...props}
                value={option.value}
                onClick={(event) => {
                  event.stopPropagation();
                  event.preventDefault();
                  handleOnChange(option);
                  setOpen(false);
                }}
              >
                {option.isCustom ? (
                  <WBFlex alignItems="center">
                    <WBIcon name="Calendar" size={2} color={'black'} />
                    <WBTypography ml={1}>{option.label}</WBTypography>
                  </WBFlex>
                ) : (
                  <WBFlex justifyContent={'space-between'} width={'100%'}>
                    <WBTypography>{option.label.split(' (')[0]}</WBTypography>
                    <WBTypography>
                      {option.label.split(' (')[1]?.replace(')', '')}
                    </WBTypography>
                  </WBFlex>
                )}
              </StyledOption>
            );
          }}
        />
        <MobileDatePicker
          sx={{ display: 'none' }}
          inputRef={inputRef}
          onAccept={(date: any) => {
            handleOnChange(date.toFormat(DATE_FORMATS.BACKEND_DATE));
          }}
        />
      </>
    );
  }
);

export default DueDateSelectorV2;

const StyledOption = styled(WBMenuItem)(({ theme }) => ({
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
}));
